import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { usersAPI, rolesAPI } from '../utils/api';
import { toast } from 'sonner';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { ShieldCheck, Info } from 'lucide-react';

const EditUserDialog = ({ user, open, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: user.nombre,
    email: user.email,
    rol_id: user.rol_id || '',
    password: '', // Optional
    tipo_documento: user.tipo_documento || '',
    documento_identidad: user.documento_identidad || '',
  });
  
  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    password_change_required: user.password_change_required || false,
    two_factor_enabled: user.two_factor_enabled || false
  });
  
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const tiposDocumento = [
    { value: 'DNI', label: 'DNI' },
    { value: 'RUC', label: 'RUC' },
    { value: 'Pasaporte', label: 'Pasaporte' },
    { value: 'Carnet de Extranjería', label: 'Carnet de Extranjería' },
  ];

  useEffect(() => {
    loadRoles();
  }, []);
  
  // Update form when user prop changes
  useEffect(() => {
    if (user && open) {
      setFormData({
        nombre: user.nombre || '',
        email: user.email || '',
        rol_id: user.rol_id || '',
        password: '',
        tipo_documento: user.tipo_documento || '',
        documento_identidad: user.documento_identidad || '',
      });
      setSecuritySettings({
        password_change_required: user.password_change_required || false,
        two_factor_enabled: user.two_factor_enabled || false
      });
    }
  }, [user, open]);

  const loadRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      setRoles(response.data);
    } catch (error) {
      toast.error('Error al cargar roles');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updateData = {
        nombre: formData.nombre,
        email: formData.email,
        rol_id: formData.rol_id,
        tipo_documento: formData.tipo_documento || null,
        documento_identidad: formData.documento_identidad || null,
        two_factor_enabled: securitySettings.two_factor_enabled,
        password_change_required: securitySettings.password_change_required,
        // password_never_expires is read-only, don't update it
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      const { data: updatedUser } = await usersAPI.update(user.id, updateData);
      toast.success('Usuario actualizado exitosamente');
      onClose(updatedUser);
    } catch (error) {
      toast.error('Error al actualizar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Editar Usuario
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Nombre Completo *</Label>
            <Input
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="border-[#c5b8a2]"
              required
            />
          </div>

          <div>
            <Label>Correo Electrónico *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="border-[#c5b8a2]"
              required
            />
          </div>

          <div>
            <Label>Nueva Contraseña (opcional)</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="border-[#c5b8a2]"
              placeholder="Dejar vacío para mantener actual"
            />
            
            {/* Indicador de fortaleza de contraseña */}
            {formData.password && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                <PasswordStrengthIndicator 
                  password={formData.password} 
                  email={formData.email}
                />
              </div>
            )}
          </div>

          <div>
            <Label>Rol *</Label>
            <Select
              value={formData.rol_id}
              onValueChange={(val) => setFormData({ ...formData, rol_id: val })}
            >
              <SelectTrigger className="border-[#c5b8a2]">
                <SelectValue placeholder="Seleccionar rol" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((rol) => (
                  <SelectItem key={rol.id} value={rol.id}>
                    {rol.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de Documento</Label>
              <Select
                value={formData.tipo_documento}
                onValueChange={(val) => setFormData({ ...formData, tipo_documento: val })}
              >
                <SelectTrigger className="border-[#c5b8a2]">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {tiposDocumento.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Documento de Identidad</Label>
              <Input
                value={formData.documento_identidad}
                onChange={(e) => setFormData({ ...formData, documento_identidad: e.target.value.slice(0, 20) })}
                className="border-[#c5b8a2]"
                maxLength={20}
                placeholder="Número de documento"
              />
            </div>
          </div>

          {/* Security Section */}
          <div className="border-t border-[#E8E8E7] pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className="text-[#9d8272]" />
              <Label className="text-base font-semibold">Seguridad</Label>
            </div>
            
            {/* Password Change Required Checkbox - Editable */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-3">
              <Checkbox
                id="edit_password_change_required"
                checked={securitySettings.password_change_required}
                onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, password_change_required: checked }))}
                data-testid="edit-checkbox-password-change-required"
              />
              <div className="flex-1">
                <Label htmlFor="edit_password_change_required" className="text-sm font-medium cursor-pointer">
                  Solicitar cambio de contraseña en el próximo inicio de sesión
                </Label>
                <p className="text-xs text-[#888888]">
                  Al iniciar sesión, el usuario será forzado a definir una nueva contraseña antes de acceder a la plataforma.
                </p>
              </div>
            </div>

            {/* Aviso: cuando ambos flags están activos, prevalece el cambio de contraseña */}
            {securitySettings.password_change_required && securitySettings.two_factor_enabled && (
              <div
                className="flex items-start gap-2 p-3 mb-3 rounded-lg border border-amber-200 bg-amber-50"
                data-testid="edit-security-priority-notice"
              >
                <Info size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  Tienes activados <strong>Cambio de contraseña</strong> y <strong>2FA por correo</strong>. En el próximo inicio de sesión prevalecerá el cambio de contraseña; el 2FA se aplicará a partir del siguiente ingreso.
                </p>
              </div>
            )}

            {/* 2FA Checkbox - Editable */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-3">
              <Checkbox
                id="edit_two_factor_enabled"
                checked={securitySettings.two_factor_enabled}
                onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, two_factor_enabled: checked }))}
                data-testid="two-factor-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="edit_two_factor_enabled" className="text-sm font-medium cursor-pointer">
                  Habilitar 2FA por correo
                </Label>
                <p className="text-xs text-[#888888]">
                  El usuario recibirá un código de verificación en su correo al iniciar sesión
                </p>
              </div>
            </div>

            {/* Password Expires Checkbox - Read Only */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-3 opacity-70">
              <Checkbox
                id="edit_password_expires"
                checked={true}
                disabled
                data-testid="password-expires-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="edit_password_expires" className="text-sm font-medium text-[#888888]">
                  Cambio obligatorio de contraseña cada 90 días
                </Label>
                <p className="text-xs text-[#888888]">
                  Política de seguridad establecida por el sistema
                </p>
              </div>
            </div>

            {/* Password Never Expires Checkbox - Read Only */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg opacity-70">
              <Checkbox
                id="edit_password_never_expires"
                checked={false}
                disabled
                data-testid="password-never-expires-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="edit_password_never_expires" className="text-sm font-medium text-[#888888]">
                  Contraseña no caduca
                </Label>
                <p className="text-xs text-[#888888]">
                  Solo puede ser modificado por un administrador del sistema
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.rol_id}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserDialog;
